/**
 * Message tool dialogs (DESIGN.md T10 "Message actions"): message details (header analysis) and
 * view source. The actions that open them are defined once in the action registry
 * (`createMessageActions`, DESIGN.md §18); this module only renders the dialogs and loads their
 * data on demand.
 */
import { MessageDetailsDialog, MessageSourceDialog, useAsync } from '@platform/ui';
import { api } from '../api';
import { ProtectedMailDialog } from './protected-mail';

export type MessageDialog = 'details' | 'source' | 'protected';

/** Details or source dialog of one message; data is loaded when the dialog opens. */
export function MessageDialogs({ messageId, subject, open, onClose }: { messageId: string; subject: string; open: MessageDialog | undefined; onClose: () => void }) {
  const analysis = useAsync(() => (open === 'details' ? api.getAnalysis(messageId) : Promise.resolve(undefined)), open === 'details' ? messageId : '');
  const source = useAsync(() => (open === 'source' ? api.getSource(messageId) : Promise.resolve(undefined)), open === 'source' ? messageId : '');
  const onOpenChange = (value: boolean) => {
    if (!value) onClose();
  };
  return (
    <>
      {open === 'protected' && <ProtectedMailDialog key={messageId} messageId={messageId} subject={subject} onClose={onClose} />}
      <MessageDetailsDialog
        open={open === 'details'}
        onOpenChange={onOpenChange}
        subject={subject}
        analysis={analysis.data}
        loading={analysis.loading}
        error={Boolean(analysis.error)}
        onRetry={analysis.reload}
      />
      <MessageSourceDialog
        open={open === 'source'}
        onOpenChange={onOpenChange}
        subject={subject}
        source={source.data}
        loading={source.loading}
        error={Boolean(source.error)}
        onRetry={source.reload}
        downloadUrl={api.sourceDownloadUrl(messageId)}
      />
    </>
  );
}
